import { Actions, createEffect, ofType } from '@ngrx/effects'
import { FolderOverviewApiMappingService, FolderOverviewApiService, FolderOverviewService } from '../services'
import { SNACKBAR_LENGTH_SHORT, SnackbarService } from '@app/shared/utils/snackbar.service'
import { catchError, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs'

import { FolderOverviewActions } from '../actions'
import { FolderOverviewSelectors } from '../selectors'
import { FolderUpdateApi } from '../models/api'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'

@Injectable()
export class FolderOverviewEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private router: Router,
    private apiService: FolderOverviewApiService,
    private apiMappingService: FolderOverviewApiMappingService,
    private folderOverviewService: FolderOverviewService,
    private snackbarService: SnackbarService
  ) {}

  getCurrentFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.getCurrentFolder),
      filter(() => !!this.folderOverviewService.folderId),
      switchMap(() =>
        this.apiService.getFolder(this.folderOverviewService.folderId).pipe(
          map((folder) => FolderOverviewActions.getCurrentFolderSuccess({ folder })),
          catchError((error: any) => of(FolderOverviewActions.getCurrentFolderFail({ error })))
        )
      )
    )
  )

  updateFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.updateFolder),
      withLatestFrom(this.store$.select(FolderOverviewSelectors.selectFolder)),
      switchMap(([{ changes }, folder]) => {
        const payload: FolderUpdateApi = this.apiMappingService.mapFolderToUpdateApi({ ...folder, ...changes })

        return this.apiService.updateFolder(folder.identity.id, payload).pipe(
          map((folder) => FolderOverviewActions.updateFolderSuccess({ folder })),
          catchError((error: any) => of(FolderOverviewActions.updateFolderFail({ error })))
        )
      })
    )
  )

  getFolderScopes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.getFolderScopes),
      filter(() => !!this.folderOverviewService.folderId),
      switchMap(() =>
        this.apiService.getFolderScopes(this.folderOverviewService.folderId).pipe(
          map((scopes) => FolderOverviewActions.getFolderScopesSuccess({ scopes })),
          catchError((error: any) => of(FolderOverviewActions.getFolderScopesFail({ error })))
        )
      )
    )
  )

  getRetainedHoursStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.getRetainedHoursStats),
      filter(() => !!this.folderOverviewService.folderId),
      switchMap(() =>
        this.apiService.getRetainedHoursStats(this.folderOverviewService.folderId).pipe(
          map((retainedHoursStats) => FolderOverviewActions.getRetainedHoursStatsSuccess({ retainedHoursStats })),
          catchError((error: any) => of(FolderOverviewActions.getRetainedHoursStatsFail({ error })))
        )
      )
    )
  )

  changeFolderVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.changeFolderVersion),
      switchMap(({ id, versionNumber }) =>
        this.apiService.getFolderVersionById(id, versionNumber).pipe(
          map((folder) => FolderOverviewActions.changeFolderVersionSuccess({ folder })),
          catchError((error) => of(FolderOverviewActions.changeFolderVersionFail({ error })))
        )
      )
    )
  )

  trafficScopes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.trafficScopes),
      filter(() => !!this.folderOverviewService.folderId),
      switchMap(({ id, scopeIds }) =>
        this.apiService.trafficScopes(id, scopeIds).pipe(
          map((trafficStatus) => FolderOverviewActions.trafficScopesSuccess({ trafficStatus })),
          catchError((error: any) => of(FolderOverviewActions.trafficScopesFail({ error })))
        )
      )
    )
  )

  deleteFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.deleteFolder),
      switchMap(({ id }) =>
        this.apiService.deleteFolder(id).pipe(
          map(() => {
            this.snackbarService.showSuccessSnackbar('Folder was successfully deleted', SNACKBAR_LENGTH_SHORT)
            return FolderOverviewActions.deleteFolderSuccess({ id })
          }),
          catchError((error) => of(FolderOverviewActions.deleteFolderFail({ error })))
        )
      )
    )
  )

  archiveFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.archiveFolder),
      switchMap(({ id }) =>
        this.apiService.archiveFolder(id).pipe(
          map(() => {
            this.snackbarService.showSuccessSnackbar('Folder was successfully archived', SNACKBAR_LENGTH_SHORT)
            return FolderOverviewActions.archiveFolderSuccess({ id })
          }),
          catchError((error) => of(FolderOverviewActions.archiveFolderFail({ error })))
        )
      )
    )
  )

  unarchiveFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderOverviewActions.unarchiveFolder),
      switchMap(({ id }) =>
        this.apiService.unarchiveFolder(id).pipe(
          map(() => {
            this.snackbarService.showSuccessSnackbar('Folder was successfully unarchived', SNACKBAR_LENGTH_SHORT)
            return FolderOverviewActions.unarchiveFolderSuccess({ id })
          }),
          catchError((error) => of(FolderOverviewActions.unarchiveFolderFail({ error })))
        )
      )
    )
  )

  navigateToFolders$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          FolderOverviewActions.deleteFolderSuccess,
          FolderOverviewActions.archiveFolderSuccess,
          FolderOverviewActions.unarchiveFolderSuccess
        ),
        tap(() => this.router.navigate(['/scopes'], { queryParams: { tab: 'folders' } }))
      ),
    { dispatch: false }
  )
}
